import { motion } from "framer-motion";
import { ArrowRight, PlayCircle, ExternalLink } from "lucide-react";
import { ScreenShell, GlassCard, GradientText, ShimmerButton, SimHeader } from "@/components/sim/shared";
import { MicroParticles } from "@/components/sim/decor";

/** YouTube watch / share / shorts → embed URL, else null. */
function youtubeEmbed(url: string): string | null {
  const m = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/.exec(url);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

/** SharePoint / OneDrive share URL → embed-friendly URL, else null. */
function sharepointEmbed(url: string): string | null {
  if (!/\.sharepoint\.com\//i.test(url) && !/1drv\.ms\//i.test(url)) return null;
  if (/embed\.aspx/i.test(url) || /action=embedview/i.test(url)) return url;
  return url + (url.includes("?") ? "&" : "?") + "action=embedview";
}

function isDirectVideo(url: string): boolean {
  return /\.(mp4|webm|ogg)(\?|$)/i.test(url);
}

export interface VideoMode {
  kind: "youtube" | "sharepoint" | "file" | "iframe";
  src: string;
}

export function resolveVideo(url: string): VideoMode {
  const yt = youtubeEmbed(url);
  if (yt !== null) return { kind: "youtube", src: yt };
  const sp = sharepointEmbed(url);
  if (sp !== null) return { kind: "sharepoint", src: sp };
  if (isDirectVideo(url)) return { kind: "file", src: url };
  return { kind: "iframe", src: url };
}

interface Props {
  sequenceNo: number;
  totalDecisions: number;
  xp: number;
  timerDisplay: string | undefined;
  videoUrl: string;
  videoTitle: string | null;
  videoInstruction: string | null;
  onContinue: () => void;
}

/**
 * VideoScreen — Lovable wrapper around resolveVideo() for the pre-decision
 * video phase. Dark glass shell, cinematic frame with conic-gradient glow,
 * gracefully handles YouTube / SharePoint Stream / direct file / generic
 * iframe sources. SharePoint sign-in fallback note for tenant-gated videos.
 */
export function VideoScreen({
  sequenceNo,
  totalDecisions,
  xp,
  timerDisplay,
  videoUrl,
  videoTitle,
  videoInstruction,
  onContinue,
}: Props): JSX.Element {
  const video = resolveVideo(videoUrl);

  return (
    <ScreenShell>
      <SimHeader decision={sequenceNo} xp={xp} timerDisplay={timerDisplay} />
      <main className="relative mx-auto max-w-5xl px-4 pb-12 md:px-8">
        <MicroParticles />

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-fuchsia-300">
                <PlayCircle className="h-4 w-4" />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em]">Watch first</span>
              </div>
              <h1 className="mt-2 text-[26px] font-bold leading-tight">
                <GradientText>{videoTitle ?? "Watch this clip"}</GradientText>
              </h1>
              {videoInstruction !== null && (
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/65">{videoInstruction}</p>
              )}
            </div>
            <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-white/55">
              Decision {sequenceNo} of {totalDecisions}
            </span>
          </div>

          {/* Video frame with conic-gradient glow + dark inner */}
          <div className="relative">
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-1 rounded-3xl opacity-50"
              style={{
                background:
                  "conic-gradient(from 90deg, rgba(139,92,246,0.6), rgba(34,211,238,0.4), rgba(232,121,249,0.5), rgba(139,92,246,0.6))",
                filter: "blur(22px)",
              }}
            />
            <div className="relative aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black shadow-2xl">
              {video.kind === "file" ? (
                <video src={video.src} controls className="h-full w-full" />
              ) : (
                <iframe
                  src={video.src}
                  title={videoTitle ?? "Video"}
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                />
              )}
            </div>
          </div>

          {/* Helper line for SharePoint videos */}
          {video.kind === "sharepoint" && (
            <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-white/45">
              <ExternalLink className="h-3 w-3" />
              If the video doesn&rsquo;t load, you may need to be signed in to your organisation, or{" "}
              <a href={videoUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-white">
                open it in a new tab
              </a>.
            </p>
          )}

          {/* Continue */}
          <div className="mt-7 flex justify-end">
            <ShimmerButton onClick={onContinue}>
              Continue <ArrowRight className="h-4 w-4" />
            </ShimmerButton>
          </div>
        </motion.div>
      </main>
    </ScreenShell>
  );
}
